import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Copy,
    Loader2,
    Mail,
    Merge as MergeIcon,
    Phone,
    RefreshCw,
} from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { findContactDuplicates, mergeContacts } from "../lib/api";

/**
 * iter-185 — Contact duplicates + merge dialog.
 * Finds groups of contacts sharing a normalised email or phone number
 * and offers a per-field merge picker so contractors can consolidate
 * duplicates without losing data. All references (leads.auto_contact_id,
 * projects.contact_id) are re-pointed at the surviving primary on the
 * server before the duplicates are deleted.
 */
const MERGEABLE = [
    { key: "name", label: "Name" },
    { key: "company", label: "Company" },
    { key: "email", label: "Email" },
    { key: "cell_phone", label: "Cell phone" },
    { key: "work_phone", label: "Work phone" },
    { key: "address", label: "Address" },
    { key: "city", label: "City" },
    { key: "country", label: "Country" },
    { key: "notes", label: "Notes" },
];

function MergeGroupCard({ group, onMerged }) {
    const [primaryId, setPrimaryId] = useState(group.contacts[0]?.id || "");
    const [fields, setFields] = useState({});
    const [busy, setBusy] = useState(false);

    const primary = useMemo(
        () => group.contacts.find((c) => c.id === primaryId),
        [group.contacts, primaryId],
    );
    const mergeIds = useMemo(
        () => group.contacts.filter((c) => c.id !== primaryId).map((c) => c.id),
        [group.contacts, primaryId],
    );

    const pickField = (key, contactId) => {
        setFields((prev) => ({ ...prev, [key]: contactId }));
    };

    const runMerge = async () => {
        if (mergeIds.length === 0) return;
        setBusy(true);
        try {
            const merged = await mergeContacts(primaryId, mergeIds, fields);
            toast.success(
                `Merged ${mergeIds.length + 1} contacts into "${merged.name || "primary"}".`,
            );
            onMerged && onMerged({ primaryId, removedIds: mergeIds, merged });
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Merge failed",
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="rounded-sm border border-[#2A3655] bg-[#0F1B3A]/60 p-3 space-y-3"
            data-testid={`dup-group-${group.kind}-${group.value}`}
        >
            <div className="flex items-center gap-2 text-xs text-slate-400 uppercase tracking-wider font-bold">
                {group.kind === "email" ? (
                    <Mail className="w-3 h-3 text-[#00A3FF]" />
                ) : (
                    <Phone className="w-3 h-3 text-emerald-400" />
                )}
                <span>{group.kind === "email" ? "Same email" : "Same phone"}</span>
                <span className="text-slate-300 lowercase tracking-normal font-normal ml-1">
                    {group.value}
                </span>
                <span className="ml-auto text-slate-500 font-normal normal-case">
                    {group.contacts.length} contacts
                </span>
            </div>

            {/* Primary picker */}
            <div className="flex items-start gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold shrink-0 mt-1">
                    Keep as primary:
                </span>
                <div className="flex items-center gap-1 flex-wrap">
                    {group.contacts.map((c) => {
                        const selected = c.id === primaryId;
                        return (
                            <button
                                key={c.id}
                                type="button"
                                onClick={() => setPrimaryId(c.id)}
                                data-testid={`dup-pick-primary-${c.id}`}
                                className={`px-2 py-1 rounded text-xs border transition-colors ${
                                    selected
                                        ? "bg-emerald-500 text-black border-emerald-400 font-semibold"
                                        : "bg-[#0A1128] text-slate-200 border-[#2A3655] hover:border-slate-400"
                                }`}
                            >
                                {c.name || "(no name)"}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Field grid */}
            <div className="overflow-x-auto -mx-1">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-slate-500 uppercase tracking-wider">
                            <th className="text-left pr-2 pb-1 font-bold min-w-[80px]">
                                Field
                            </th>
                            {group.contacts.map((c) => (
                                <th
                                    key={c.id}
                                    className={`text-left pr-2 pb-1 font-bold min-w-[130px] ${
                                        c.id === primaryId ? "text-emerald-300" : ""
                                    }`}
                                >
                                    {c.name || "(no name)"}
                                    {c.id === primaryId && " ★"}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="text-slate-200">
                        {MERGEABLE.map(({ key, label }) => {
                            const values = group.contacts.map((c) => (c[key] || "").trim());
                            const anyValue = values.some((v) => v);
                            if (!anyValue) return null;
                            const chosenId = fields[key] || primaryId;
                            const primaryValue = primary?.[key];
                            return (
                                <tr
                                    key={key}
                                    className="border-t border-[#2A3655]/50"
                                    data-testid={`dup-field-row-${key}`}
                                >
                                    <td className="pr-2 py-1.5 text-slate-400 text-[10px] uppercase tracking-wider font-bold align-top">
                                        {label}
                                    </td>
                                    {group.contacts.map((c) => {
                                        const val = (c[key] || "").trim();
                                        const isChosen = chosenId === c.id;
                                        const disabled = !val;
                                        return (
                                            <td
                                                key={c.id}
                                                className="pr-2 py-1 align-top"
                                            >
                                                <button
                                                    type="button"
                                                    disabled={disabled}
                                                    onClick={() => pickField(key, c.id)}
                                                    data-testid={`dup-cell-${key}-${c.id}`}
                                                    className={`w-full text-left px-2 py-1 rounded border text-xs transition-colors ${
                                                        isChosen && val
                                                            ? "bg-emerald-500/15 border-emerald-400 text-white"
                                                            : val
                                                                ? "bg-[#0A1128] border-[#2A3655] hover:border-slate-400 text-slate-200"
                                                                : "bg-[#0A1128]/40 border-transparent text-slate-600 cursor-not-allowed"
                                                    }`}
                                                >
                                                    {val || "—"}
                                                </button>
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between pt-1 border-t border-[#2A3655]">
                <div className="text-[11px] text-slate-500">
                    {mergeIds.length + 1 - 1} contacts will be merged into
                    {" "}
                    <span className="text-emerald-300 font-semibold">
                        {primary?.name || "primary"}
                    </span>
                </div>
                <Button
                    type="button"
                    onClick={runMerge}
                    disabled={busy}
                    size="sm"
                    data-testid={`dup-merge-btn-${primaryId}`}
                    className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold h-8"
                >
                    {busy ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                        <>
                            <MergeIcon className="w-3 h-3 mr-1" />
                            Merge {mergeIds.length + 1} into 1
                        </>
                    )}
                </Button>
            </div>
        </div>
    );
}

export default function ContactDuplicatesDialog({
    open,
    onOpenChange,
    onChanged,
}) {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);

    const reload = async () => {
        setLoading(true);
        try {
            const rows = await findContactDuplicates();
            setGroups(rows);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't load duplicates");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open) return;
        reload();
    }, [open]);

    const handleMerged = ({ removedIds, merged }) => {
        // Locally drop groups that fully collapsed after the merge.
        setGroups((prev) =>
            prev
                .map((g) => ({
                    ...g,
                    contacts: g.contacts
                        .filter((c) => !removedIds.includes(c.id))
                        .map((c) => (c.id === merged.id ? merged : c)),
                }))
                .filter((g) => g.contacts.length >= 2),
        );
        onChanged && onChanged({ removedIds, merged });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-3xl bg-[#0A1128] border-[#2A3655] text-white"
                data-testid="contact-duplicates-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Copy className="w-4 h-4 text-[#00A3FF]" />
                        Duplicate contacts
                        <button
                            type="button"
                            onClick={reload}
                            disabled={loading}
                            data-testid="dup-refresh"
                            title="Re-scan"
                            className="ml-2 p-1 rounded hover:bg-white/5 text-slate-400"
                        >
                            <RefreshCw
                                className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}
                            />
                        </button>
                    </DialogTitle>
                    <DialogDescription className="text-slate-400 text-sm">
                        Contacts that share a normalised email or phone. Pick a
                        primary, choose which value to keep for each field, and
                        merge. Every lead and project referencing a duplicate
                        will be re-pointed at the primary automatically.
                    </DialogDescription>
                </DialogHeader>

                <div className="max-h-[65vh] overflow-y-auto space-y-3 -mx-1 px-1">
                    {loading && (
                        <div className="flex items-center gap-2 text-slate-400 text-sm py-6 justify-center">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Scanning…
                        </div>
                    )}
                    {!loading && groups.length === 0 && (
                        <div className="text-center py-8 text-slate-400 text-sm">
                            No duplicates. Your contact list is clean.
                        </div>
                    )}
                    {groups.map((g) => (
                        <MergeGroupCard
                            key={`${g.kind}-${g.value}`}
                            group={g}
                            onMerged={handleMerged}
                        />
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
}
